import { FormGroup } from '@angular/forms';
import {
    FieldBase,
    ProductBase,
    WorkflowConfig,
    WorkflowConfigField,
    WorkflowConfigSection,
    WorkflowConfigStep
} from '@monorepo/interfaces';
import { WorkflowFormTransformerOperations } from './WorkflowFormTransformerOperations';

/**
 * Abstract base processor for workflow form data processing operations.
 *
 * @description This abstract class provides the foundation for different workflow processing strategies
 * (create, patch, etc.). It contains shared logic for traversing workflow configuration structure
 * and collecting form data while allowing concrete implementations to specify type-specific operations.
 *
 * @typeParam T - The result object type (e.g. CreateWorkflowData or PatchWorkflowData)
 * @typeParam F - The field type which must extend FieldBase (e.g. CreateField or Field)
 * @typeParam P - The product type which must extend ProductBase (e.g. CreateProduct or Product)
 */
export abstract class BaseWorkflowProcessor<T, F extends FieldBase, P extends ProductBase> {
    /**
     * The value of the coreMlfb control as found during traversal.
     * This property is updated during field step/section collection for correct product mapping.
     * @protected
     */
    protected coreMlfbControlValue: string;

    /**
     * Sets the current value of the coreMlfb control.
     * @param {string} value - The value to assign as current coreMlfb.
     * @returns {void}
     */
    public setCoreMlfbControlValue(value: string): void {
        this.coreMlfbControlValue = value;
    }
    /**
     * Creates a field object based on field configuration and form value
     *
     * @param fieldConfig {WorkflowConfigField} - The configuration object defining the field structure
     * @param value {any} - The value extracted from the form control
     * @returns A field object of type F with the extracted value
     */
    abstract createFieldObject(fieldConfig: WorkflowConfigField, value: unknown): F;

    /**
     * Creates a product object containing the specified fields
     *
     * @param fields {F[]} - Array of field objects to include in the product
     * @param name {string} - The name of the product
     * @param existingProduct {P} - Optional existing product to reference for additional data (like ID)
     * @returns A product object of type P containing the provided fields
     */
    abstract createProductObject(fields: F[], name: string, existingProduct?: P): P;

    /**
     * Creates the final result object to be returned from the processor
     *
     * @param config {WorkflowConfig} - The workflow configuration used for processing
     * @param fields {F[]} - Collection of processed field objects
     * @param products {P[]} - Collection of processed product objects
     * @returns The final structured object of type T for submission to the backend
     */
    abstract createResultObject(config: WorkflowConfig, fields: F[], products: P[]): T;

    /**
     * Initializes an empty array for collecting field objects.
     * @abstract
     * @returns {F[]} An array of field objects of type F filled with initial values (empty for Create) .
     */
    abstract getWorkflowDetailsFields(): F[];

    /**
     * Initializes an empty array for collecting product objects.
     * @abstract
     * @returns {Map<string, P>} A map of product objects of type P, keyed by product name (empty for Create).
     */
    abstract getWorkflowDetailsProducts(): Map<string, P>;

    /**
     * Adds a product-relevant field to the products' collection.
     * @abstract
     * @param {F} field - The processed field object with its extracted form value.
     * @param {Map<string, P>} products - The map of products to add/merge the field into.
     * @param {FormGroup} productFormGroup - The form group containing the product fields.
     * @returns {void}
     */
    protected abstract addFieldToProduct(field: F, products: Map<string, P>, productFormGroup?: FormGroup): void;

    /**
     * Processes the workflow form data and transforms it into a structured format for submission
     *
     * @description This method takes a workflow configuration and its corresponding form group,
     * extracts all field values from the form (including nested steps and sections),
     * and organizes them into a structured object ready for submission to the backend.
     * It handles both regular fields and product-related fields separately according to the
     * processor implementation type (create or patch).
     *
     * @param config {WorkflowConfig} - The workflow configuration containing structure definition
     * @param workflowFormGroup {FormGroup} - The Angular form group containing all form values
     * @returns A structured object with collected field values ready for submission
     */
    public processWorkflowDetailsForm(config: WorkflowConfig, workflowFormGroup: FormGroup): T {
        const fields: F[] = this.getWorkflowDetailsFields();
        const products = this.getWorkflowDetailsProducts();

        // Process top-level workflow fields
        this.collectFields(config.fields || [], workflowFormGroup, fields, products);

        // Process steps and nested sections
        if (config.steps?.length) {
            const stepsGroup = workflowFormGroup.get('steps') as FormGroup;
            const productsGroup = workflowFormGroup.get('products') as FormGroup;
            config.steps.forEach(step => {
                const stepGroup = stepsGroup.get(`step-${step.name}`) as FormGroup;
                this.collectStepOrSection(step, stepGroup, fields, products, productsGroup);
            });
        }

        return this.createResultObject(config, fields, Array.from(products.values()));
    }

    /**
     * Processes form fields and categorizes them into fields or products arrays
     *
     * @description This method iterates through field configurations, extracts values from the
     * corresponding form controls, and places each field into either the general fields array
     * or the products array based on the field's product relevance flag. This segregation
     * allows product-specific and general workflow fields to be processed differently during
     * workflow submission.
     *
     * @param fieldConfigs {WorkflowConfigField[]} - Array of field configuration objects to process
     * @param formGroup {FormGroup} - The Angular form group containing the field values
     * @param productsGroup {FormGroup} - The Angular form group containing the field values for products
     * @param fields {F[]} - Output array where non-product-relevant fields will be collected
     * @param products {Map<string, P>} - Output map where product-relevant fields will be collected per product
     * @returns {void}
     */
    protected collectFields(
        fieldConfigs: WorkflowConfigField[],
        formGroup: FormGroup,
        fields: F[],
        products: Map<string, P>,
        productsGroup?: FormGroup
    ): void {
        fieldConfigs.forEach(field => {
            const control = formGroup.get(field.name);
            if (!control) return;

            const fieldObject = this.createFieldObject(
                field,
                WorkflowFormTransformerOperations.getFieldValueStringOrCommaSeparatedString(field, control.value) ??
                    field.default ??
                    ''
            );

            if (field.productRelevant) {
                this.addFieldToProduct(fieldObject, products, productsGroup);
            } else {
                this.mergeField(fields, fieldObject);
            }
        });
    }

    /**
     * Merges a field into the fields array
     *
     * @description By default, just pushes the field (Create behavior).
     * Subclasses like PatchWorkflowProcessor can override this to replace existing fields.
     *
     * @param fields {F[]} - The current collection of fields
     * @param newField {F} - The new field to merge
     */
    protected mergeField(fields: F[], newField: F): void {
        fields.push(newField);
    }

    /**
     * Recursively processes a step or section and its children to collect form values
     *
     * @description This method extracts field values from a step or section form group and
     * processes them into the fields and products arrays. It then recursively processes any
     * nested sections within the current step/section, enabling the extraction of values from
     * a deeply nested form structure that matches the workflow configuration hierarchy.
     *
     * @param config {WorkflowConfigStep | WorkflowConfigSection} - The step or section configuration object
     * @param formGroup {FormGroup} - The Angular form group corresponding to this step or section
     * @param productsGroup {FormGroup} - The Angular form group with all product-related fields
     * @param fields {F[]} - Output array where non-product-relevant fields will be collected
     * @param products {Map<string, P>} - Output map where product-relevant fields will be collected per product
     * @returns {void}
     */
    protected collectStepOrSection(
        config: WorkflowConfigStep | WorkflowConfigSection,
        formGroup: FormGroup,
        fields: F[],
        products: Map<string, P>,
        productsGroup?: FormGroup
    ): void {
        this.collectFields(config.fields || [], formGroup, fields, products, productsGroup);

        if ('sections' in config && config.sections?.length) {
            const sectionsGroup = formGroup.get('sections') as FormGroup;
            config.sections.forEach(section => {
                const sectionGroup = sectionsGroup.get(`section-${section.name}`) as FormGroup;
                const coreMlfbControl = sectionGroup.get('core-mlfb');

                if (coreMlfbControl) {
                    this.setCoreMlfbControlValue(coreMlfbControl.value);
                }

                // Or search the entire form recursively
                this.findAndSetCoreMlfbControl(formGroup);
                this.collectStepOrSection(section, sectionGroup, fields, products, productsGroup);
            });
        }
    }

    /**
     * Validates the form groups for all visible workflow steps
     *
     * @param formGroup {FormGroup} - The main form group containing all workflow data
     * @param filteredWorkflowConfigSteps {WorkflowConfigStep[]} - Array of workflow steps that are currently visible/accessible
     * @returns {boolean} - True if all visible steps are valid, false otherwise
     */
    validateWorkflowForm(formGroup: FormGroup, filteredWorkflowConfigSteps: WorkflowConfigStep[]): boolean {
        if (!formGroup || !filteredWorkflowConfigSteps?.length) {
            return false;
        }
        const stepsGroup = formGroup.get('steps') as FormGroup;
        const productsGroup = formGroup.get('products') as FormGroup;

        // Validate all steps and sections
        const stepsValid = filteredWorkflowConfigSteps.every(step => {
            const stepGroup = stepsGroup.get(`step-${step.name}`) as FormGroup;
            stepGroup.markAllAsTouched();
            if (stepGroup.disabled) {
                return true;
            }
            return this.validateStepOrSection(step, stepGroup);
        });

        if (!stepsValid) {
            return false;
        }

        // Validate product fields only if the steps being validated contain product-relevant fields
        if (productsGroup && this.stepsHaveProductRelevantFields(filteredWorkflowConfigSteps)) {
            const productFieldNames = this.collectProductRelevantFieldNames(filteredWorkflowConfigSteps);
            return this.validateProductFields(productsGroup, productFieldNames);
        }

        return true;
    }

    /**
     * Checks whether any of the given steps (or their nested sections) contain product-relevant fields
     */
    private stepsHaveProductRelevantFields(steps: WorkflowConfigStep[]): boolean {
        return steps.some(step => this.configHasProductRelevantFields(step));
    }

    private configHasProductRelevantFields(config: WorkflowConfigStep | WorkflowConfigSection): boolean {
        if (config.fields?.some(field => field.productRelevant)) {
            return true;
        }
        if ('sections' in config && config.sections?.length) {
            return config.sections.some(section => this.configHasProductRelevantFields(section));
        }
        return false;
    }

    /**
     * Collects all product-relevant field names from the given steps and their nested sections
     */
    private collectProductRelevantFieldNames(steps: WorkflowConfigStep[]): Set<string> {
        const names = new Set<string>();
        for (const step of steps) {
            this.collectProductFieldNamesFromConfig(step, names);
        }
        return names;
    }

    private collectProductFieldNamesFromConfig(
        config: WorkflowConfigStep | WorkflowConfigSection,
        names: Set<string>
    ): void {
        config.fields?.forEach(field => {
            if (field.productRelevant) {
                names.add(field.name);
            }
        });
        if ('sections' in config && config.sections?.length) {
            config.sections.forEach(section => this.collectProductFieldNamesFromConfig(section, names));
        }
    }

    /**
     * Recursively validates a workflow sections
     *
     * @param config {WorkflowConfigStep | WorkflowConfigSection} - The step or section configuration to validate
     * @param formGroup {FormGroup} - The form group corresponding to the step or section
     * @returns {boolean} - True if all visible sections are valid, false otherwise
     */
    validateStepOrSection(config: WorkflowConfigStep | WorkflowConfigSection, formGroup: FormGroup): boolean {
        // validate only sections, but not steps
        if (!formGroup.valid && !('sections' in config)) {
            return false;
        }
        // Recursively validate nested sections if they exist
        if ('sections' in config && config.sections?.length) {
            const sectionsGroup = formGroup.get('sections') as FormGroup;

            for (const section of config.sections) {
                const sectionGroup = sectionsGroup.get(`section-${section.name}`) as FormGroup;
                sectionGroup.markAllAsTouched();

                if (!sectionGroup.valid) {
                    return false; // exits the outer function immediately
                }

                // recursively validate nested sections or steps
                const valid = this.validateStepOrSection(section, sectionGroup);
                if (!valid) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Validates specific product fields within each product FormGroup
     *
     * @param productsGroup {FormGroup} - The form group containing all products (product-{name})
     * @param fieldNames {Set<string>} - The set of field names to validate within each product
     * @returns {boolean} - True if all specified fields in all products are valid, false otherwise
     */
    private validateProductFields(productsGroup: FormGroup, fieldNames: Set<string>): boolean {
        for (const productKey of Object.keys(productsGroup.controls)) {
            const productGroup = productsGroup.get(productKey) as FormGroup;
            if (productGroup) {
                for (const fieldName of fieldNames) {
                    const fieldControl = productGroup.get(fieldName);
                    if (fieldControl) {
                        fieldControl.markAsTouched();
                        if (fieldControl.invalid) {
                            return false;
                        }
                    }
                }
            }
        }
        return true;
    }

    /**
     * Recursively searches for coreMlfb control and sets its value.
     * @param {FormGroup} group - The form group to search.
     * @returns {void}
     */
    private findAndSetCoreMlfbControl(group: FormGroup): void {
        if (group.controls['core-mlfb']) {
            this.setCoreMlfbControlValue(group.controls['core-mlfb'].value);
            return;
        }

        Object.keys(group.controls).forEach(key => {
            const control = group.get(key);
            if (control instanceof FormGroup) {
                this.findAndSetCoreMlfbControl(control);
            }
        });
    }
}
